import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, FileText, Send, X, Wallet } from 'lucide-react';
import PageHeader from './common/PageHeader';
import PriceListModal from './PriceListModal';
import PaymentModal from './partner/PaymentModal';
import ReceivableLedgerPanel from './partner/ReceivableLedgerPanel';
import { getLabBilling, setClosingDay, partnerKeyOf, isMemberPartnerKey, kstYmd } from '../utils/billingCycle';
import { loadReceivables, invalidateReceivables } from '../utils/receivables';
import { invalidateSummaryCache } from './statement/SummaryCards';

// 거래처(치과) 현황 (2026-07-30 기획서 4절) — 3경로(웹/파일/이메일)로 흩어진 치과를 한 화면에
// 2026-10-23 거래명세서 1단계(명세 §3-2·§4-6·§5-5, 계약 §4 C):
//   표 열 = 치과명 | [입금 기록] | 청구 마감 | 받을 돈 | 이번 달 발행 | 단가표 | 가입
//   '들어오는 경로'·'최근 의뢰'는 치과 이름을 누르면 열리는 패널로. 기본 정렬 = 받을 돈 많은 순.
//   받을 돈은 이 화면(PIN 구역, 대표 전용 — App.js /partners 라우트)에서만 보인다.
// 병합 규칙은 명세서 거래처 목록과 동일 (이름 정규화 — 공백·괄호·'치과' 접미 제거)

const normBiz = (n) => (n || '').replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();
const CLOSING_OPTIONS = [['', '없음'], ...Array.from({ length: 28 }, (_, i) => [String(i + 1), `${i + 1}일`]), ['end', '말일']];

function PartnerOverview({ user }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState('all');    // all | nonmember | noprice
    const [priceClinic, setPriceClinic] = useState(null);   // {id, name} — 단가표 관리 모달
    const [recvError, setRecvError] = useState('');          // 받을 돈 계산 실패 안내 (조용한 실패 금지)
    const [manualCount, setManualCount] = useState(0);       // 직접 입력 명세서(받는 곳 키 없음) 장수
    const [panelRow, setPanelRow] = useState(null);           // 치과 이름을 누르면 열리는 패널
    const [payRow, setPayRow] = useState(null);               // 입금 기록 창
    const [savingClosing, setSavingClosing] = useState({});   // partnerKey → true (마감일 저장 중)
    const [ledgerVer, setLedgerVer] = useState(0);            // 입금 저장 뒤 원장 패널 다시 읽기 신호

    const uid = user?.companyId || auth.currentUser?.uid;

    const load = useCallback(async (force = false) => {
        if (!uid) return;
        setLoading(true);
        try {
            const map = new Map();   // key: 회원 id 또는 norm 이름 → row
            const byNorm = new Map();  // normBiz(name) → key (병합용)

            const ensure = (key, name, patch) => {
                const k = byNorm.get(normBiz(name)) || key;
                if (!map.has(k)) {
                    map.set(k, {
                        id: k, name, member: false, connected: false,
                        sources: new Set(), lastOrderAt: 0, monthCount: 0, priceCount: 0,
                    });
                    byNorm.set(normBiz(name), k);
                }
                const row = map.get(k);
                Object.assign(row, patch);
                return row;
            };

            // 0) 받을 돈(명세서·계산서·입금은 receivables 가 한 번만 읽음)·청구 마감일 — 다른 조회와 병렬 (실패는 각각 화면에 알림)
            const recvP = loadReceivables(uid, { force: force === true }).catch(e => ({ byPartner: {}, total: 0, error: e?.message || String(e) }));
            const billingP = getLabBilling(uid).catch(e => { console.warn('청구 마감일 조회 실패:', e?.code || e?.message); return { partners: {}, error: true }; });

            // 1) 연결된 거래처 (회원)
            const [sentSnap, recvSnap] = await Promise.all([
                getDocs(query(collection(db, 'connections'), where('requesterId', '==', uid), where('status', '==', 'accepted'))),
                getDocs(query(collection(db, 'connections'), where('receiverId', '==', uid), where('status', '==', 'accepted'))),
            ]);
            const partnerIds = new Set();
            [...sentSnap.docs, ...recvSnap.docs].forEach(d => {
                const c = d.data();
                const pid = c.requesterId === uid ? c.receiverId : c.requesterId;
                if (pid) partnerIds.add(pid);
            });
            for (const pid of partnerIds) {
                try {
                    const u = await getDoc(doc(db, 'users', pid));
                    if (!u.exists()) continue;
                    const d = u.data();
                    if (d.businessType === 'lab') continue;   // 기공소 간 연결 제외 (치과 계정 businessType='dental')
                    ensure(pid, d.businessName || d.name || '이름 없음', { member: true, connected: true });
                } catch (e) { console.warn('거래처 계정 조회 실패:', pid, e?.code || e?.message); }
            }

            // 2) 이메일 매핑 치과
            try {
                const ls = await getDoc(doc(db, 'labSettings', uid));
                const mapping = ls.exists() ? (ls.data().clinicEmailMapping || {}) : {};
                for (const [clinicId, data] of Object.entries(mapping)) {
                    const name = data.clinicName || '';
                    if (!name) continue;
                    const row = map.has(clinicId)
                        ? map.get(clinicId)
                        : ensure(`email_${name}`, name, {});
                    row.sources.add('email');
                }
            } catch (e) { console.warn('이메일 설정 조회 실패:', e?.code || e?.message); }

            // 3) 파일 수집(parsedCases) 치과 + 파일 경로 최근/건수
            const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
            try {
                const ps = await getDocs(query(collection(db, 'parsedCases'),
                    where('labId', '==', uid), where('status', 'in', ['pending', 'imported'])));
                ps.docs.forEach(d => {
                    const r = d.data();
                    const name = (r.clientNameInFile || '').trim();
                    if (!name) return;
                    const row = ensure(`agent_${name}`, name, {});
                    row.sources.add('file');
                    const t = r.fileDate?.seconds ? r.fileDate.seconds * 1000
                        : (r.createdAt?.seconds ? r.createdAt.seconds * 1000 : 0);
                    if (t > row.lastOrderAt) row.lastOrderAt = t;
                    if (t >= monthStart.getTime()) row.monthCount += 1;
                });
            } catch (e) { console.warn('수집 치과 조회 실패:', e?.code || e?.message); }

            // 4) 웹 의뢰서 — 경로·최근·건수 (agent_auto는 3에서 집계했으므로 제외)
            try {
                const os = await getDocs(query(collection(db, 'workOrders'), where('toUserId', '==', uid)));
                os.docs.forEach(d => {
                    const o = d.data();
                    if (o.source === 'agent_auto') return;
                    const name = o.fromUserName || '';
                    const key = o.fromUserId || (name ? `order_${name}` : null);
                    if (!key) return;
                    const row = map.get(key) || (name ? ensure(key, name, {}) : null);
                    if (!row) return;
                    // source: email=이메일 수신 의뢰 / dentweb·미기재=웹 작성
                    row.sources.add(o.source === 'email' ? 'email' : 'web');
                    const t = o.createdAt?.seconds ? o.createdAt.seconds * 1000 : 0;
                    if (t > row.lastOrderAt) row.lastOrderAt = t;
                    if (t >= monthStart.getTime()) row.monthCount += 1;
                });
            } catch (e) { console.warn('의뢰서 조회 실패:', e?.code || e?.message); }

            // 5) 단가표 — id 일치 + 이름 일치(미가입 이름 키·키 분열 흡수, 2026-07-30 B안)
            try {
                const pl = await getDocs(query(collection(db, 'priceLists'), where('userId', '==', uid)));
                const byName = new Map();
                pl.docs.forEach(d => {
                    const p = d.data();
                    const row = map.get(p.partnerId);
                    if (row) row.priceCount = Math.max(row.priceCount, (p.items || []).length);
                    if (p.partnerName) byName.set(normBiz(p.partnerName), (p.items || []).length);
                });
                map.forEach(row => {
                    if (!row.priceCount && byName.has(normBiz(row.name))) row.priceCount = byName.get(normBiz(row.name));
                });
            } catch (e) { console.warn('단가표 조회 실패:', e?.code || e?.message); }

            // 6) 받을 돈·마감일·이번 달 발행을 거래처 키(partnerKey = 명세서 받는 곳 키)로 모은다
            const [recv, billing] = await Promise.all([recvP, billingP]);
            const errs = [];
            if (recv?.error) { console.warn('받을 돈 계산 실패:', recv.error); errs.push('받을 돈·이번 달 발행'); }
            if (billing?.error) errs.push('청구 마감일');
            setRecvError(errs.length ? `${errs.join('·')}을(를) 불러오지 못했습니다. 새로고침해 주세요.` : '');

            const byKey = new Map();   // partnerKey → { partnerKey, name, recv, closingDay, monthIssued, hasDraft }
            const touch = (pk, name) => {
                if (!pk) return null;
                if (!byKey.has(pk)) byKey.set(pk, { partnerKey: pk, name: name || '', recv: null, closingDay: null, monthIssued: 0, monthIssuedCount: 0, hasDraft: false });
                const e = byKey.get(pk);
                if (!e.name && name) e.name = name;
                return e;
            };
            const thisMonth = kstYmd(new Date()).slice(0, 7);   // KST 'YYYY-MM'
            // 이번 달 발행 = 살아 있는 발행본 중 귀속일(KST)이 이번 달인 것 / 작성 중 = 초안 장수 — 모두 receivables 결과로 계산(명세서 다시 읽지 않음)
            setManualCount(Number(recv?.manualCount) || 0);
            Object.values(recv?.byPartner || {}).forEach(r => {
                const e = touch(r.partnerKey, r.partnerName);
                if (!e) return;
                e.recv = r;
                (r.statements || []).forEach(st => {
                    if (String(st.date || '').slice(0, 7) !== thisMonth) return;
                    e.monthIssued += Number(st.amount) || 0;
                    e.monthIssuedCount += 1;
                });
                e.hasDraft = (Number(r.draftCount) || 0) > 0;
            });
            Object.entries(billing?.partners || {}).forEach(([pk, b]) => {
                const e = touch(pk, b?.partnerName);
                if (e) e.closingDay = b?.closingDay ?? null;
            });

            // 거래처 키 → 표 줄 연결: ① 키가 같은 줄 ② 이름이 같은(정규화) 줄. 한 줄에 키 하나만 —
            // 같은 이름의 두 번째 키(예: 가입 전 비회원 이름 키)는 돈 기록을 합치지 않고 따로 한 줄로 보여 준다(명세 §3-6 "자동으로 합치지 않음").
            const list = Array.from(map.values());
            const rowByNorm = new Map();
            const rowByPk = new Map();   // 줄에서 나오는 정본 거래처 키 → 줄
            list.forEach(r => {
                if (!rowByNorm.has(normBiz(r.name))) rowByNorm.set(normBiz(r.name), r);
                const pk = partnerKeyOf(r.id, r.name);
                if (pk && !rowByPk.has(pk)) rowByPk.set(pk, r);
            });
            const extra = [];
            const entries = Array.from(byKey.values());
            const attach = (row, e) => { row.partnerKey = e.partnerKey; row.money = e; };
            entries.forEach(e => { const row = rowByPk.get(e.partnerKey); if (row && !row.money) attach(row, e); else if (row) extra.push(e); });
            entries.forEach(e => {
                if (rowByPk.get(e.partnerKey)?.money === e || extra.includes(e)) return;
                const row = rowByNorm.get(normBiz(e.name));
                if (row && !row.money) attach(row, e); else extra.push(e);
            });
            extra.forEach(e => {
                const isMemberKey = isMemberPartnerKey(e.partnerKey);
                const base = rowByNorm.get(normBiz(e.name));
                list.push({
                    id: `pk:${e.partnerKey}`, name: e.name || e.partnerKey.replace(/^name:/, ''),
                    member: isMemberKey, connected: false, sources: base ? base.sources : new Set(),
                    lastOrderAt: base ? base.lastOrderAt : 0, monthCount: 0, priceCount: base ? base.priceCount : 0,
                    partnerKey: e.partnerKey, money: e, baseId: base ? base.id : null,
                    keyNote: base ? (isMemberKey ? '회원 계정 기록' : '가입 전 이름 기록') : '',
                });
            });
            // 돈 기록이 없는 줄의 거래처 키 = 정본 규칙(billingCycle.partnerKeyOf — 회원 uid, 비회원은 'name:'+정규화 이름)
            list.forEach(r => {
                if (!r.partnerKey) r.partnerKey = partnerKeyOf(r.id, r.name);
                if (!r.money) r.money = { partnerKey: r.partnerKey, recv: null, closingDay: null, monthIssued: 0, monthIssuedCount: 0, hasDraft: false };
            });

            const bal = (r) => r.money?.recv?.balance || 0;
            list.sort((a, b) => (bal(b) - bal(a)) || (b.money.monthIssued - a.money.monthIssued)
                || (b.monthCount - a.monthCount) || (b.lastOrderAt - a.lastOrderAt));
            setRows(list);
        } catch (e) {
            console.error('거래처 현황 로드 실패:', e?.code || e?.message);
            alert('거래처 현황을 불러오지 못했습니다. 새로고침해 주세요.');
        } finally {
            setLoading(false);
        }
    }, [uid]);

    useEffect(() => { load(); }, [load]);

    // 받을 돈만 다시 계산 (입금 기록·취소 뒤) — 표 전체를 다시 읽지 않는다
    const refreshReceivables = useCallback(async () => {
        try {
            invalidateReceivables(uid);   // 입금 기록·취소 뒤 세션 캐시 버리고 다시 계산
            invalidateSummaryCache(uid);  // 명세서 목록 카드 ③·'입금' 열도 다음에 다시 계산
            const recv = await loadReceivables(uid, { force: true });
            if (recv?.error) {
                console.warn('받을 돈 계산 실패:', recv.error);
                setRecvError('받을 돈을 불러오지 못했습니다. 새로고침해 주세요.');
                return;
            }
            setRows(prev => prev.map(r => {
                const e = recv.byPartner?.[r.partnerKey];
                return { ...r, money: { ...r.money, recv: e || null } };
            }));
        } catch (e) {
            console.error('받을 돈 다시 계산 실패:', e?.code || e?.message);
            setRecvError('받을 돈을 다시 계산하지 못했습니다. 새로고침해 주세요.');
        }
    }, [uid]);

    const changeClosing = async (r, value) => {
        const pk = r.partnerKey;
        if (!pk || savingClosing[pk]) return;
        const next = value === '' ? null : (value === 'end' ? 'end' : Number(value));
        const prevVal = r.money.closingDay;
        setSavingClosing(p => ({ ...p, [pk]: true }));
        setRows(prev => prev.map(x => x.partnerKey === pk ? { ...x, money: { ...x.money, closingDay: next } } : x));
        try {
            await setClosingDay(uid, pk, r.name, next);
        } catch (e) {
            console.error('청구 마감일 저장 실패:', e?.code || e?.message);
            setRows(prev => prev.map(x => x.partnerKey === pk ? { ...x, money: { ...x.money, closingDay: prevVal } } : x));
            alert('청구 마감일을 저장하지 못했습니다. 잠시 후 다시 골라 주세요.');
        } finally {
            setSavingClosing(p => { const n = { ...p }; delete n[pk]; return n; });
        }
    };

    // 최근 의뢰가 2년 이상 지난 치과는 숨긴다 (2026-10-21 대표 지시).
    // 의뢰 기록이 없는 치과(lastOrderAt=0 — 새로 초대·연결만 된 경우)는 "지났다" 대상이 아니므로 유지.
    // 받을 돈·이번 달 발행·작성 중·청구 마감일이 있는 치과는 의뢰가 없어도 보인다 (2026-10-23 — 돈이 숨으면 안 됨)
    const staleCutoff = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 2); return d.getTime(); })();
    const hasMoney = (r) => !!(r.money?.recv?.balance || r.money?.monthIssued || r.money?.hasDraft || r.money?.closingDay);
    const filtered = rows.filter(r => {
        if (!hasMoney(r)) {
            if (r.lastOrderAt && r.lastOrderAt < staleCutoff) return false;
            if (!r.monthCount) return false;   // 이번 달 의뢰 0건 치과는 숨김 (2026-10-21 대표 지시)
        }
        return filter === 'nonmember' ? !r.member :
            filter === 'noprice' ? (r.member && r.priceCount === 0) : true;
    });

    const srcBadge = (s) => s === 'web' ? { t: '웹 의뢰서', bg: '#EEF2FF', c: '#4F46E5' }
        : s === 'file' ? { t: '파일 수집', bg: '#ECFDF5', c: '#047857' }
        : { t: '이메일', bg: '#FFF7ED', c: '#C2410C' };

    const fmtDate = (t) => t ? new Date(t).toLocaleDateString('ko-KR', { year: 'numeric', month: 'numeric', day: 'numeric' }) : '-';
    const won = (n) => `${(Number(n) || 0).toLocaleString()}`;

    const renderBalance = (r) => {
        const rv = r.money?.recv;
        if (!rv) return <span style={{ fontSize: 13, color: '#CBD5E1' }}>{recvError ? '-' : '0'}</span>;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                {rv.balance > 0
                    ? <span style={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>{won(rv.balance)}</span>
                    : rv.balance < 0
                        ? <span style={{ fontSize: 12.5, fontWeight: 700, color: '#475569' }}>{`받을 돈 없음 · ${won(-rv.balance)}원 더 받음`}</span>
                        : <span style={{ fontSize: 13, color: '#94A3B8' }}>0</span>}
                {rv.taxNeedsReview && (
                    <span title="면세 기공물 명세서에 과세 세금계산서가 붙어 있습니다. 세무사에게 확인해 주세요."
                        style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A', whiteSpace: 'nowrap' }}>
                        {'과세 계산서 있음 — 세무사에게 확인'}
                    </span>
                )}
            </div>
        );
    };

    const renderMonth = (r) => {
        const m = r.money || {};
        if (m.monthIssued > 0) {
            return <span style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{`${won(m.monthIssued)} ✓`}{m.hasDraft && <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#B45309' }}>{'+ 작성 중'}</span>}</span>;
        }
        if (m.hasDraft) return <span style={{ fontSize: 12.5, fontWeight: 700, color: '#B45309' }}>{'작성 중'}</span>;
        return <span style={{ fontSize: 13, color: '#CBD5E1' }}>{'-'}</span>;
    };

    const th = (align) => ({ padding: '11px 12px', textAlign: align, fontSize: 12, fontWeight: 700, color: '#64748B', whiteSpace: 'nowrap' });

    return (
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <PageHeader title="거래처 현황" subtitle="치과별 받을 돈·청구 마감·이번 달 발행을 한눈에 봅니다" />

            <div style={{ display: 'flex', gap: 8, margin: '14px 0', alignItems: 'center', flexWrap: 'wrap' }}>
                {[['all', '전체'], ['nonmember', '미가입만'], ['noprice', '단가표 없음']].map(([k, label]) => (
                    <button key={k} onClick={() => setFilter(k)}
                        style={{ padding: '7px 14px', fontSize: 13, fontWeight: 700, borderRadius: 18, cursor: 'pointer',
                            border: `1px solid ${filter === k ? '#4F46E5' : '#E2E8F0'}`,
                            background: filter === k ? '#EEF2FF' : '#fff', color: filter === k ? '#4F46E5' : '#64748B' }}>
                        {label}
                    </button>
                ))}
                <span style={{ flex: 1 }} />
                <button onClick={() => load(true)} disabled={loading}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', fontSize: 13,
                        border: '1px solid #E2E8F0', borderRadius: 8, background: '#fff', color: '#475569', cursor: 'pointer' }}>
                    <RefreshCw size={14} /> 새로고침
                </button>
            </div>

            {recvError && (
                <div style={{ marginBottom: 10, padding: '9px 12px', fontSize: 13, fontWeight: 600, color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8 }}>
                    {recvError}
                </div>
            )}

            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#94A3B8' }}>불러오는 중...</div>
            ) : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#94A3B8', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12 }}>
                    표시할 치과가 없습니다. 의뢰서를 받거나 파일 수집을 실행하면 여기에 나타납니다.
                </div>
            ) : (
                <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                                <th style={th('left')}>{'치과명'}</th>
                                {/* [입금 기록]은 치과명 바로 옆(2열) — 가장 자주 누르는 버튼이 맨 끝에 있어 폰에서 안 보였음 (2026-10-24 재점검 P2-16) */}
                                <th style={th('left')}></th>
                                <th style={th('left')} title="매달 이 날까지 한 작업을 한 장으로 묶습니다">{'청구 마감'}</th>
                                <th style={th('right')}>{'받을 돈'}</th>
                                <th style={th('right')}>{'이번 달 발행'}</th>
                                <th style={th('left')}>{'단가표'}</th>
                                <th style={th('left')}>{'가입'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(r => (
                                <tr key={r.id} style={{ borderBottom: '1px solid #F1F5F9', background: panelRow?.id === r.id ? '#F8FAFF' : undefined }}>
                                    <td style={{ padding: '11px 12px' }}>
                                        <button onClick={() => setPanelRow(r)} title="원장·들어오는 경로·최근 의뢰를 봅니다"
                                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontSize: 14, fontWeight: 700, color: '#1E3A8A', textDecoration: 'underline', textDecorationColor: '#C7D2FE', textUnderlineOffset: 3 }}>
                                            {r.name}
                                        </button>
                                        {r.keyNote && <span style={{ display: 'block', fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{r.keyNote}</span>}
                                    </td>
                                    <td style={{ padding: '11px 8px', whiteSpace: 'nowrap' }}>
                                        {r.partnerKey && (
                                            <button onClick={() => setPayRow(r)}
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5, fontWeight: 700, color: '#fff', background: '#4F46E5', border: 'none', borderRadius: 8, padding: '6px 11px', cursor: 'pointer' }}>
                                                <Wallet size={13} /> {'입금 기록'}
                                            </button>
                                        )}
                                    </td>
                                    <td style={{ padding: '11px 12px', whiteSpace: 'nowrap' }}>
                                        {r.partnerKey ? (
                                            <select value={r.money.closingDay == null ? '' : String(r.money.closingDay)}
                                                disabled={!!savingClosing[r.partnerKey]}
                                                onChange={e => changeClosing(r, e.target.value)}
                                                style={{ padding: '6px 8px', fontSize: 13, borderRadius: 8, border: '1px solid #E2E8F0', background: '#fff', color: r.money.closingDay == null ? '#94A3B8' : '#0F172A', fontWeight: 600 }}>
                                                {CLOSING_OPTIONS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                                            </select>
                                        ) : <span style={{ fontSize: 12, color: '#CBD5E1' }}>{'-'}</span>}
                                    </td>
                                    <td style={{ padding: '11px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{renderBalance(r)}</td>
                                    <td style={{ padding: '11px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{renderMonth(r)}</td>
                                    <td style={{ padding: '11px 12px', whiteSpace: 'nowrap' }}>
                                        <button onClick={() => setPriceClinic({ id: r.id.startsWith('pk:') ? (r.baseId || (r.member ? r.partnerKey : `agent_${r.name}`)) : r.id, name: r.name })}
                                            title="단가표 관리 창을 엽니다"
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: r.priceCount > 0 ? '#4F46E5' : '#DC2626', background: r.priceCount > 0 ? '#EEF2FF' : '#FEF2F2', border: `1px solid ${r.priceCount > 0 ? '#C7D2FE' : '#FECACA'}`, borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>
                                            <FileText size={12} /> {r.priceCount > 0 ? `${r.priceCount}개` : '없음'}
                                        </button>
                                    </td>
                                    <td style={{ padding: '11px 12px', whiteSpace: 'nowrap' }}>
                                        {r.member
                                            ? <span style={{ fontSize: 12, fontWeight: 700, color: '#047857' }}>{'회원'}</span>
                                            : <button onClick={() => navigate('/clinic-invites')}
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '4px 9px', cursor: 'pointer' }}>
                                                <Send size={12} /> {'비회원 — 초대'}
                                            </button>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p style={{ fontSize: 12.5, color: '#475569', marginTop: 10, fontWeight: 600 }}>
                {'입금은 직접 기록한 것만 반영됩니다. 받을 돈 = 보낸 명세서 합계 + 발행한 과세 세금계산서 세액 − 기록한 입금 (작성 중 명세서는 빠짐)'}
            </p>
            {manualCount > 0 && (
                <p style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>{`직접 입력 명세서 ${manualCount}장은 받을 돈에 안 들어갑니다.`}</p>
            )}
            <p style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
                이름이 같은 치과는 경로가 달라도 한 줄로 합쳐 보입니다. 미가입 치과의 단가표는 치과 이름 기준으로 저장되며, 가입하면 자동 승계됩니다. 최근 의뢰가 2년 이상 지난 치과, 이번 달 의뢰가 0건인 치과는 받을 돈·발행 기록이 없으면 목록에서 숨겨집니다.
            </p>

            {panelRow && (
                <div onClick={() => setPanelRow(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.25)', zIndex: 900 }}>
                    <aside onClick={e => e.stopPropagation()}
                        style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(560px, 100%)', background: '#fff', boxShadow: '-8px 0 24px rgba(0,0,0,0.12)', overflowY: 'auto', padding: 20, boxSizing: 'border-box' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0F172A', flex: 1 }}>{panelRow.name}</h3>
                            <button onClick={() => setPanelRow(null)} aria-label="닫기" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', padding: 4 }}><X size={18} /></button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '96px 1fr', rowGap: 8, fontSize: 13, color: '#334155', padding: 12, background: '#F8FAFC', borderRadius: 10, marginBottom: 16 }}>
                            <span style={{ color: '#64748B', fontWeight: 700 }}>{'들어오는 경로'}</span>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {[...panelRow.sources].map(s => {
                                    const b = srcBadge(s);
                                    return <span key={s} style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 10, background: b.bg, color: b.c }}>{b.t}</span>;
                                })}
                                {panelRow.sources.size === 0 && <span style={{ fontSize: 12, color: '#CBD5E1' }}>{'기록 없음'}</span>}
                            </div>
                            <span style={{ color: '#64748B', fontWeight: 700 }}>{'최근 의뢰'}</span>
                            <span>{fmtDate(panelRow.lastOrderAt)}</span>
                            <span style={{ color: '#64748B', fontWeight: 700 }}>{'이번 달 의뢰'}</span>
                            <span>{`${panelRow.monthCount}건`}</span>
                        </div>
                        {panelRow.partnerKey ? (
                            <ReceivableLedgerPanel
                                labId={uid}
                                partnerKey={panelRow.partnerKey}
                                partnerName={panelRow.name}
                                receivable={rows.find(x => x.id === panelRow.id)?.money?.recv || null}
                                onAddPayment={() => setPayRow(rows.find(x => x.id === panelRow.id) || panelRow)}
                                onChanged={refreshReceivables}
                                reloadKey={ledgerVer}
                            />
                        ) : (
                            <p style={{ fontSize: 13, color: '#94A3B8' }}>{'이 치과는 명세서 거래처로 연결되지 않아 원장을 볼 수 없습니다.'}</p>
                        )}
                    </aside>
                </div>
            )}

            {payRow && (
                <PaymentModal labId={uid} partnerKey={payRow.partnerKey} partnerName={payRow.name}
                    balance={payRow.money?.recv?.balance}
                    onClose={() => setPayRow(null)}
                    onSaved={() => { setLedgerVer(v => v + 1); refreshReceivables(); }} />
            )}

            {priceClinic && (
                <PriceListModal user={user} clinicId={priceClinic.id} clinicName={priceClinic.name}
                    onClose={() => setPriceClinic(null)} onSaved={() => load()} />
            )}
        </div>
    );
}

export default PartnerOverview;
